import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { Observable } from 'rxjs/internal/Observable';
import { lastValueFrom } from 'rxjs/internal/lastValueFrom';

// import { CredentialingActivity, User } from '@app/models';
import { CredentialingChecklistItem } from '@app/modules/credentialing/credentialing-setups/credentialing-setups.service';
import { SharedUtilsService } from './shared-utils.service';

export interface CredentialingRequiredItemsApi {
  items: any[];
  total_count: number;
}

@Injectable({ providedIn: 'root' })
export class CredentialingRequiredItemsService {
  private currentRequiredItem;

  public currentSearch = {
    params: null,
    results: null
  };

  private API_URL = '/api/credentialing-required-items/';

  private headerOptions = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };

  constructor(
    private httpClient: HttpClient,
    public _sharedUtilsService: SharedUtilsService
  ) { }

  setCurrent(_requiredItem): void {
    this.currentRequiredItem = _requiredItem;
  }

  getCurrent() {
    return this.currentRequiredItem;
  }

  create(_requiredItem: CredentialingChecklistItem): Observable<any> {
    return this.httpClient.post<any>(this.API_URL, JSON.stringify(_requiredItem), this.headerOptions);
  }


  saveMultiple(_requiredItems: CredentialingChecklistItem[]): Observable<any[]> {
    return this.httpClient.post<any[]>(this.API_URL + 'saveMultiple', JSON.stringify(_requiredItems), this.headerOptions);
  }

  updateItemOrders(_requiredItems: CredentialingChecklistItem[]): Observable<any[]> {
    return this.httpClient.post<any[]>(this.API_URL + 'updateItemOrders', JSON.stringify(_requiredItems), this.headerOptions);
  }


  update(_requiredItem: CredentialingChecklistItem): Observable<any> {
    return this.httpClient.put<any>(this.API_URL + _requiredItem._id, JSON.stringify(_requiredItem), this.headerOptions);
  }


  search(searchTerms): Observable<any[]> {
    searchTerms['selectAll'] = true;
    return this.httpClient.post<any[]>(this.API_URL + 'dataSourceSearch', JSON.stringify(searchTerms), this.headerOptions);
  }


  normalSearch(searchTerms): Observable<any[]> {
    searchTerms['selectAll'] = true;
    return this.httpClient.post<any[]>(this.API_URL + 'normal-search', JSON.stringify(searchTerms), this.headerOptions);
  }


  getCredentialingItemRecord(_id: string): Observable<any> {
    return this.httpClient.get<any>(this.API_URL + _id);
  }


  markDeleted(id: string): Observable<any> {
    return this.httpClient.put(this.API_URL + 'mark-deleted/' + id, this.headerOptions);
  }


  delete(id: string): Observable<any> {
    return this.httpClient.delete<any>(this.API_URL + id);
  }


  selectAllSearch(searchTerms): Observable<CredentialingChecklistItem[]> {
    searchTerms['selectAll'] = true;
    return this.httpClient.post<CredentialingChecklistItem[]>(this.API_URL + 'dataSourceSearch', JSON.stringify(searchTerms), this.headerOptions);
  }


  getCustomDatasource(searchParams, sortField = 'name', sortDirection = 'asc', pageNumber: number = 0, pageSize: number = 10): Observable<CredentialingRequiredItemsApi> {
    searchParams['selectAll'] = false;

    searchParams.sortField = sortField;
    searchParams.sortOrder = sortDirection;
    searchParams.pageNumber = pageNumber;
    searchParams.pageSize = pageSize;

    return this.httpClient.post<CredentialingRequiredItemsApi>(this.API_URL + 'dataSourceSearch', JSON.stringify(searchParams), this.headerOptions);
  }


  createRequiredItems(_itemsToCreate: CredentialingChecklistItem[], _credentialingRecordId): Promise<any> {
    return new Promise(async (resolve) => {
      let createdItems = [];

      if (_itemsToCreate?.length && _credentialingRecordId) {
        const defaultDetails = {
          expirationDate: null,
          approvalStatus: '',
          documentLink: null,
          actionValue: null,
          notes: '',
          dateCompleted: null
        };

        const preparedItems: CredentialingChecklistItem[] = [];
        const itemIdMap = new Map<string, CredentialingChecklistItem>();

        // First pass: create new item objects and build an ID map to support condition remapping logic.
        for (const _item of _itemsToCreate) {
          const completionDetailsForItem = (_item?.completionDetails) ? _item.completionDetails : {...defaultDetails};

          // console.log('completionDetailsForItem: ', completionDetailsForItem);

          const newRequiredItem = new CredentialingChecklistItem();

          newRequiredItem._id = this._sharedUtilsService.createObjectId();
          newRequiredItem.deleted = false;
          newRequiredItem.order = _item.order;
          newRequiredItem.name = _item?.label ?? 'Unknown';
          newRequiredItem.label = _item?.label ?? 'Unknown';
          newRequiredItem.category = _item?.category ?? null;
          newRequiredItem.provider_assigned = _item?.provider_assigned ?? false;
          newRequiredItem.description = _item?.description ?? '';
          newRequiredItem.completed = _item?.completed ?? false;
          newRequiredItem.completionDetails = completionDetailsForItem;
          newRequiredItem.field_type = _item?.field_type ?? null;
          newRequiredItem.field_options = _item?.field_options ?? null;
          newRequiredItem.provider_type = _item?.provider_type ?? null;
          newRequiredItem.relatedCredentialingRecord = _credentialingRecordId;
          newRequiredItem.unreadMessageCount = 0;
          newRequiredItem.messages = [];
          newRequiredItem.teams = (_item?.teams?.length) ? _item.teams.map((_t) => _t?._id ? _t._id : _t) : [];
          newRequiredItem.conditions = _item?.conditions ?? null;
          newRequiredItem.locked = (_item?.conditions?.length) ? true : false;
          newRequiredItem.requires_exp_date = (_item?.requires_exp_date) ? true : false;
          newRequiredItem.dropdown = _item?.dropdown?._id ?? _item?.dropdown ?? null;

          if (_item?.isOptional) newRequiredItem.isOptional = true;
          if (_item?.reference_only) newRequiredItem.reference_only = true;

          if (_item?.field_type === 'Yeehro Sign Document') {
            const attachedAgreement = _item?.relatedAgreement ?? _item?.completionDetails?.actionValue ?? null;
            newRequiredItem.relatedAgreement = attachedAgreement?._id ?? attachedAgreement;
          }

          preparedItems.push(newRequiredItem);
          itemIdMap.set(_item?._id?.toString(), newRequiredItem); // Map original item's _id to the new item for condition reference resolution.
        }

        // Second pass: reassign relatedRequiredItemId in conditions to point to the correct new item _id.
        for (let i = 0; i < _itemsToCreate.length; i++) {
          const originalItem = _itemsToCreate[i]; // The original item from the input array.
          const targetItem = preparedItems[i]; // The corresponding new item we are preparing to save.

          if (originalItem?.conditions?.length) {
            // Update any condition's relatedRequiredItemId to point to the new item if possible.
            const updatedConditions = originalItem.conditions.map((condition) => {
              if (condition?.relatedRequiredItemId) {
                const relatedItem = itemIdMap.get(condition.relatedRequiredItemId.toString()); // Get the new item that corresponds to the original item _id from _itemsToCreate.
                if (relatedItem) return { ...condition, relatedRequiredItemId: relatedItem._id }; // Return condition with the updated relatedRequiredItemId pointing to new correct item _id.
              }
              return condition; // No change needed; return the original condition.
            });

            targetItem.conditions = updatedConditions; // Apply updated conditions to the new item.
          }
        }

        // Final save: persist all items after condition references have been updated.
        const savedItems = await lastValueFrom(this.saveMultiple(preparedItems));
        createdItems = (savedItems?.length) ? savedItems : [];
      }

      resolve(createdItems);
    });
  }
}